import React from 'react';
import { Tooltip, TooltipProps } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Z_INDEX_TOOLTIP_DEFAULT } from '@/constants/zIndex';

interface AppTooltipProps extends Omit<TooltipProps, 'label'> {
  /**
   * Translation key for the tooltip text, or direct text if no translation needed
   */
  labelKey?: string;

  /**
   * Direct label text (use when translation is not needed)
   */
  label?: string;

  /**
   * Translation interpolation values
   */
  values?: Record<string, string | number>;

  /**
   * Children to wrap with tooltip
   */
  children: React.ReactNode;
}

/**
 * Standardized tooltip component for the GeoMarkup application
 *
 * Features:
 * - Consistent styling matching the language selector tooltip
 * - Proper z-index to appear above all panels and modals
 * - Automatic translation support
 * - Fallback to direct text when needed
 */
export const AppTooltip: React.FC<AppTooltipProps> = ({
  labelKey,
  label,
  values,
  children,
  position = 'bottom',
  withArrow = true,
  openDelay = 500,
  zIndex = Z_INDEX_TOOLTIP_DEFAULT,
  ...props
}) => {
  const { t } = useTranslation();

  // Determine the label to use
  const tooltipLabel = labelKey ? t(labelKey, values) : label;

  // Don't render tooltip if no label is provided
  if (!tooltipLabel) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      label={tooltipLabel}
      position={position}
      withArrow={withArrow}
      openDelay={openDelay}
      zIndex={zIndex}
      {...props}
    >
      {children}
    </Tooltip>
  );
};

